import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n/i18n";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_cloudship-azure/artifacts/kktuux3j_cloudship_cloud_icon.svg";

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const sectionLinks = [
    { id: "products", label: t("nav.products") },
    { id: "services", label: t("nav.services") },
    { id: "methodology", label: t("nav.methodology") },
    { id: "about", label: t("nav.about") },
    { id: "contact", label: t("nav.contact") },
  ];

  const goToSection = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "cs-glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img
            src={LOGO_URL}
            alt="Cloudship"
            className="h-10 w-10 object-contain"
          />
          <span className="font-display font-semibold text-lg tracking-wide text-white">
            Cloudship
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-8">
          {sectionLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => goToSection(l.id)}
              className="text-sm text-slate-300 hover:text-white cs-link-underline transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-blog"
            className="text-sm text-slate-300 hover:text-white cs-link-underline transition-colors"
          >
            {t("nav.blog")}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex items-center gap-1 text-xs font-medium border border-slate-700/70 rounded-full p-1"
            data-testid="lang-toggle"
          >
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1 rounded-full transition-colors ${
                lang === "en" ? "bg-sky-400 text-[#0d1722]" : "text-slate-300 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-toggle-de"
              onClick={() => setLang("de")}
              className={`px-3 py-1 rounded-full transition-colors ${
                lang === "de" ? "bg-sky-400 text-[#0d1722]" : "text-slate-300 hover:text-white"
              }`}
            >
              DE
            </button>
          </div>

          <button
            onClick={() => goToSection("contact")}
            data-testid="header-cta-button"
            className="cs-btn-primary text-sm hidden sm:inline-flex"
          >
            {t("nav.cta")}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden cs-glass border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {sectionLinks.map((l) => (
              <button
                key={l.id}
                onClick={() => goToSection(l.id)}
                className="text-left text-base text-slate-200"
                data-testid={`mobile-nav-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <Link to="/blog" className="text-base text-slate-200" data-testid="mobile-nav-blog">
              {t("nav.blog")}
            </Link>
            <div className="flex items-center gap-2 pt-2">
              <button
                data-testid="mobile-lang-en"
                onClick={() => setLang("en")}
                className={`px-3 py-1 text-xs rounded-full border ${
                  lang === "en" ? "bg-sky-400 border-sky-400 text-[#0d1722]" : "border-slate-600 text-slate-200"
                }`}
              >
                EN
              </button>
              <button
                data-testid="mobile-lang-de"
                onClick={() => setLang("de")}
                className={`px-3 py-1 text-xs rounded-full border ${
                  lang === "de" ? "bg-sky-400 border-sky-400 text-[#0d1722]" : "border-slate-600 text-slate-200"
                }`}
              >
                DE
              </button>
            </div>
            <button
              onClick={() => goToSection("contact")}
              data-testid="mobile-cta"
              className="cs-btn-primary text-sm mt-2"
            >
              {t("nav.cta")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
